/**
 * lib/brokerCreds.ts -- which broker credential goes on which request.
 *
 * PURE: no imports, no storage, no fetch. Every caller passes the unlocked
 * vault map in, so the decisions below can be EXECUTED by _test/ in plain node
 * rather than only read (a syntax check is not a run).
 *
 * ONE CREDENTIAL, ONE BROKER (router WIRE-C3, B6).
 *
 * api() used to attach the ACTIVE vault entry as an unscoped
 *
 *     x-broker-key / x-broker-secret / x-broker-mode
 *
 * to every account read, whatever broker the request was about. The router
 * fanned that one key out to alpaca, ibkr AND tradovate, so an Alpaca secret
 * was sent to two third parties on every blotter refresh. The router now
 * answers an unscoped x-broker-key with 400 BROKER_HEADER_UNSCOPED on every
 * route that reads broker headers (terminalRoutes.ts readsBrokerHeaders /
 * unscopedBrokerKey). What it reads instead (credsFromHeaders):
 *
 *     x-broker-<alpaca|ibkr|tradovate>-key / -secret / -mode
 *
 * This module never emits the legacy header. It emits a broker's named
 * headers only on a request that targets that broker, and never one broker's
 * key under another broker's name.
 */

export type BrokerName = 'alpaca' | 'ibkr' | 'tradovate';
export const BROKER_NAMES: readonly BrokerName[] = ['alpaca', 'ibkr', 'tradovate'];

export function isBrokerName(v: unknown): v is BrokerName {
  return typeof v === 'string' && (BROKER_NAMES as readonly string[]).includes(v);
}

export type UnlockedMap = Record<string, { key: string; secret: string | null }>;

/**
 * Which vault entry belongs to which broker, and whether it is live.
 *
 * vault.html stores entries under the names the user picked -- `alpaca`,
 * `alpaca_paper`, `ibkr`, `tradovate_live`, `tradovate_demo` -- while the
 * router routes on `alpaca` | `ibkr` | `tradovate`.
 *
 * `mode` here is what the CLIENT sends as the entry's mode. It is NOT what the
 * ticket displays: the paper/live label comes from the router's
 * tradingModeFor() over these same credentials (GET /api/broker/status), the
 * one source the order gate also uses. IBKR's `live` is sent for
 * completeness; the router ignores it (an IBKR token cannot be proven paper,
 * so the router treats it as real money regardless).
 */
export const VAULT_ENTRY_MAP: Record<string, { broker: BrokerName; mode: 'live' | 'paper' }> = {
  alpaca:          { broker: 'alpaca',    mode: 'live'  },
  alpaca_paper:    { broker: 'alpaca',    mode: 'paper' },
  ibkr:            { broker: 'ibkr',      mode: 'live'  },
  tradovate_live:  { broker: 'tradovate', mode: 'live'  },
  tradovate_demo:  { broker: 'tradovate', mode: 'paper' },
  tradovate:       { broker: 'tradovate', mode: 'paper' },
};

/**
 * The mode string a broker's adapter understands. The router honours only the
 * exact string "live"; anything else is paper (Alpaca) or demo (Tradovate).
 */
export function wireModeFor(broker: BrokerName, mode: 'live' | 'paper'): 'live' | 'paper' | 'demo' {
  if (mode === 'live') return 'live';
  return broker === 'tradovate' ? 'demo' : 'paper';
}

/**
 * Parse the session value vault.html writes. Two shapes:
 *   v2  { v: 2, active, brokers: { <entry>: { key, secret } } }   (2026-08-03+)
 *   v1  { broker, key, secret }                                   (a tab opened before)
 * Reading only v2 would make an old tab look LOCKED, which is
 * indistinguishable from "no credentials" and silently changes whose account
 * a read describes.
 */
export function parseVaultSession(raw: string | null | undefined): UnlockedMap {
  if (!raw) return {};
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return {}; }
  const p = parsed as { v?: unknown; brokers?: unknown; broker?: unknown; key?: unknown; secret?: unknown } | null;
  if (p?.v === 2 && p.brokers && typeof p.brokers === 'object') {
    const out: UnlockedMap = {};
    for (const [name, e] of Object.entries(p.brokers as Record<string, { key?: unknown; secret?: unknown }>)) {
      if (e && typeof e.key === 'string' && e.key) {
        out[name] = { key: e.key, secret: typeof e.secret === 'string' ? e.secret : null };
      }
    }
    return out;
  }
  if (typeof p?.broker === 'string' && typeof p?.key === 'string' && p.key) {
    return { [p.broker]: { key: p.key, secret: typeof p.secret === 'string' ? p.secret : null } };
  }
  return {};
}

export interface ResolvedEntry {
  label:    string;               // the vault entry name, e.g. "alpaca_paper"
  broker:   BrokerName;
  key:      string;
  secret:   string | null;
  mode:     'live' | 'paper';     // the entry's recorded mode
  wireMode: 'live' | 'paper' | 'demo';
}

/**
 * The ONE unlocked entry for `broker`, or why there is none.
 *
 * Both a live and a paper entry unlocked for one broker is AMBIGUOUS and is
 * refused: picking one is a guess about whether the trader meant real money.
 */
export function entryForBroker(unlocked: UnlockedMap, broker: BrokerName):
  { entry: ResolvedEntry; problem: null } | { entry: null; problem: 'none' | 'ambiguous'; names: string[] } {
  const names = Object.keys(unlocked).filter((n) => VAULT_ENTRY_MAP[n]?.broker === broker && !!unlocked[n]?.key);
  if (names.length === 0) return { entry: null, problem: 'none', names };
  if (names.length > 1)   return { entry: null, problem: 'ambiguous', names };
  const label = names[0];
  const meta = VAULT_ENTRY_MAP[label];
  const e = unlocked[label];
  return {
    entry: { label, broker, key: e.key, secret: e.secret ?? null, mode: meta.mode, wireMode: wireModeFor(broker, meta.mode) },
    problem: null,
  };
}

/**
 * Destinations for POST /api/orders/multi, from the brokers unlocked in this
 * session.
 *
 * TWO REFUSALS, BOTH DELIBERATE.
 *
 * 1. A selected broker with no unlocked entry is MISSING and the caller must
 *    not fire. The router refuses it too (DESTINATION_NOT_CONNECTED).
 * 2. A selected broker with BOTH a live and a paper entry unlocked is
 *    AMBIGUOUS, and ambiguity is also refused.
 */
export function destinationsFor(unlocked: UnlockedMap, selected: string[]): {
  destinations: Array<{ label: string; broker: BrokerName; key: string; secret: string | null; mode: string }>;
  missing: string[];
} {
  const destinations: Array<{ label: string; broker: BrokerName; key: string; secret: string | null; mode: string }> = [];
  const missing: string[] = [];
  for (const want of selected) {
    if (!isBrokerName(want)) { missing.push(want); continue; }
    const matches = Object.keys(unlocked).filter((name) => VAULT_ENTRY_MAP[name]?.broker === want && !!unlocked[name]?.key);
    if (matches.length === 0) { missing.push(want); continue; }
    if (matches.length > 1) {
      // Naming both is more useful than naming the conflict.
      missing.push(`${want} (both ${matches.join(' and ')} are unlocked -- lock one)`);
      continue;
    }
    const r = entryForBroker(unlocked, want);
    if (!r.entry) { missing.push(want); continue; }
    destinations.push({ label: r.entry.label, broker: want, key: r.entry.key, secret: r.entry.secret, mode: r.entry.wireMode });
  }
  return { destinations, missing };
}

/** Named headers for exactly these brokers. Ambiguous or absent entries send nothing. */
export function brokerHeadersFor(unlocked: UnlockedMap, brokers: readonly BrokerName[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const b of brokers) {
    const r = entryForBroker(unlocked, b);
    if (!r.entry) continue;
    out[`x-broker-${b}-key`] = r.entry.key;
    if (r.entry.secret) out[`x-broker-${b}-secret`] = r.entry.secret;
    out[`x-broker-${b}-mode`] = r.entry.wireMode;
  }
  return out;
}

/**
 * Which brokers a request TARGETS -- the only brokers whose credentials it may
 * carry. Mirrors the router's readsBrokerHeaders():
 *
 *   GET /api/portfolio, /api/portfolio/account, /api/orders, /api/broker/status
 *       The router fans out over every broker the caller presented
 *       (ownBrokerCreds) and sends each credential only to its own broker, so
 *       every unlocked broker is a target, each under its own name.
 *   GET /api/level2/:symbol?broker=X, DELETE /api/orders/:id?broker=X
 *       One venue: X. With no explicit, valid ?broker= the request targets
 *       no broker we can name, so it carries NO credential (the router then
 *       routes by symbol and answers with the venue it chose; callers
 *       re-request with ?broker=<that venue>). Guessing the venue here is how a
 *       key ends up at the wrong broker.
 *   Anything else: none.
 */
export function targetBrokersFor(method: string, pathWithQuery: string): BrokerName[] {
  const m = method.toUpperCase();
  const q = pathWithQuery.indexOf('?');
  const path = q === -1 ? pathWithQuery : pathWithQuery.slice(0, q);
  const params = new URLSearchParams(q === -1 ? '' : pathWithQuery.slice(q + 1));
  const named = (params.get('broker') ?? '').toLowerCase();
  const one = (): BrokerName[] => (isBrokerName(named) ? [named] : []);
  if (m === 'GET') {
    if (path === '/api/portfolio' || path === '/api/portfolio/account'
        || path === '/api/orders' || path === '/api/broker/status') return [...BROKER_NAMES];
    if (/^\/api\/level2\/[^/]+$/.test(path)) return one();
    return [];
  }
  if (m === 'DELETE' && /^\/api\/orders\/[^/]+$/.test(path)) return one();
  return [];
}

/** The credential headers a request may carry. */
export function credentialHeadersFor(method: string, pathWithQuery: string, unlocked: UnlockedMap): Record<string, string> {
  return brokerHeadersFor(unlocked, targetBrokersFor(method, pathWithQuery));
}

/** Append ?broker= (or &broker=) to a path, replacing nothing else. */
export function withBroker(path: string, broker: BrokerName | null | undefined): string {
  if (!broker) return path;
  return path + (path.includes('?') ? '&' : '?') + 'broker=' + encodeURIComponent(broker);
}
